import ServiceHandler from '../service-handler';
import { DSL_API_PATH, DSL_BASE_URL, BOTH_OPEN_AND_COMPLETED_RECALLS, BRAND } from '../../constants';
import HttpService from '../http-service/http-service';
import { notify } from '../slack-message-service/SlackMessageService';
import { RECALLS } from '../../support/support-constants';
import { getAemVehicleModelData } from '../../hooks/use-aem-vehicle-model-data';
import {
    LANG_CODES_CARIBBEAN,
    LANG_CODES_CENTRAL_AMERICA,
} from '../../../src/support/support-constants';
import { AxiosRequestConfig, AxiosResponse } from 'axios';

export interface RecallItems {
    description: string;
    descriptionLang: string;
    fsaNumber: string;
    nhtsaRecallNumber: string;
    remedyDescription?: string;
    remedyDescriptionLang?: string;
    safetyRiskDescription?: string;
    safetyRiskDescriptionLang?: string;
    manufacturerRecallNumber: string;
    recallDescription?: string;
    recallDescriptionLang: string;
    recallDate: string;
    launchDate?: string;
    remedyAvailable?: boolean;
    manufacturerRecallStatus?: string;
    recallStatus?: string;
    govtRecallNumber?: string;
}

export interface RecallsApiResponse {
    successfulResponse: boolean;
    error: string;
    totalFsaCount: number;
    recallsCount: number;
    cspCount: number;
    vehicleDetails: {
        vin: string;
        make: string;
        model: string;
        year: number;
    };
    state: string;
    country: string;
    language: string;
    langScript: string;
    region: string;
    recalls: RecallItems[];
    fsa: RecallItems[];
    nhtsa: RecallItems[];
    ymm?: CampaignIdApiResponse[];
}

export interface CampaignIdApiResponse {
    localFsa: string;
    fsaType: string;
    fsaTypeDtl: string;
    fsaDesc: string;
    localFsaDescInEnglish: string;
    localFsaLaunchDate: string;
    localFsaDescLocal: string | null;
    govtRecallNumber: string;
    govtRecallDate: string;
    govtRecallDescEnglish: string;
    govtRecallSafetyRiskDescEnglish: string;
    govtRecallRemedyDescEnglish: string;
    govtRecallRemedyStatusFlag: string | null;
    govtRecallDescLocal: string | null;
    govtRecallSafetyRiskDescLocal: string | null;
    govtRecallRemedyDescLocal: string | null;
    govtRecallManufacturingDescLocal: string | null;
    vehicleLineCdAndYear: {};
    hub: string | null;
    error?: string;
}

export interface TradeInLinkResponse {
    tradeInLink: string;
}

export default class RecallsService {
    async request(vin: string, timeout?: boolean): Promise<RecallsApiResponse> {
        const {
            current3LetterCountryCode,
            currentLangScript,
            currentLanguageCode,
            currentLanguageRegionCode,
            currentCountryCode,
            brand,
        } = ServiceHandler.AppConfigurationService;

        const languageCodes = LANG_CODES_CARIBBEAN.CARIBBEAN_MARKETS_ENGLISH.includes(
            currentLanguageRegionCode
        )
            ? 'en-us'
            : currentLanguageRegionCode;

        const checkForLanguageCode =
            LANG_CODES_CENTRAL_AMERICA.CENTRAL_AMERICA_MARKETS_SPANISH.includes(
                currentLanguageRegionCode
            ) ||
            LANG_CODES_CARIBBEAN.CARIBBEAN_MARKETS_SPANISH.includes(
                currentLanguageRegionCode
            );

        const languageRegionCode = checkForLanguageCode
            ? 'es-mx'
            : languageCodes;
        const isLincolnNA =
            brand?.includes(BRAND.lincoln.LONG_NAME) &&
            ['ca', 'us'].includes(currentCountryCode?.toLowerCase());
        const params: Record<string, string> = {
            country: current3LetterCountryCode,
            langscript: currentLangScript,
            language: currentLanguageCode,
            region: languageRegionCode,
        };
        if (isLincolnNA) {
            params.vehStatus = BOTH_OPEN_AND_COMPLETED_RECALLS;
        }
        const headers = { vin: vin };
        const config: AxiosRequestConfig = { params, headers };
        if (timeout) {
            config.timeout = 3000;
            config.timeoutErrorMessage = 'Request Timed Out';
        }
        const recallsUrl = DSL_BASE_URL + DSL_API_PATH.RECALLS_VIN;
        const response = HttpService.get<RecallsApiResponse>(
            recallsUrl,
            true,
            config
        );
        response.catch(error => {
            console.error(error);

            return error;
        });

        return response.then(response => {
            let sendSlackAlert = false;
            sendSlackAlert = this.nhtsaResponse(
                response,
                sendSlackAlert,
                currentLanguageCode
            );
            response.data.recalls.forEach(recalls => {
                if (
                    recalls.descriptionLang === null ||
                    !recalls.descriptionLang
                ) {
                    recalls.descriptionLang = recalls.description;
                    sendSlackAlert = currentLanguageCode !== 'en';
                }
                if (recalls.remedyAvailable === undefined && recalls.manufacturerRecallStatus) {
                    recalls.remedyAvailable = recalls.manufacturerRecallStatus.includes('11');
                }
            });
            response.data.fsa.forEach(fsa => {
                if (fsa.descriptionLang === null || !fsa.descriptionLang) {
                    fsa.descriptionLang = fsa.description;
                    sendSlackAlert = currentLanguageCode !== 'en';
                }
            });
            sendSlackAlert &&
                notify(
                    `Recalls Description Language is blank for ${currentLanguageRegionCode}`,
                    RECALLS.SLACK_ALERT_RECALLS_AEM_API
                );
            return response.data;
        });
    }

    async getCampaignId(campaignId: string): Promise<CampaignIdApiResponse> {
        const {
            current3LetterCountryCode,
            currentLanguageRegionCode,
            brand,
        } = ServiceHandler.AppConfigurationService;
        const params = {
            campaignId,
            country: current3LetterCountryCode,
            brand: brand,
            region: currentLanguageRegionCode,
        };

        const campaignIdUrl = DSL_BASE_URL + DSL_API_PATH.CAMPAIGN_ID;
        const response = HttpService.get<CampaignIdApiResponse>(
            campaignIdUrl,
            true,
            {
                params,
            }
        );
        response.catch(error => {
            console.error(error);
        });
        return response.then(response => {
            return response.data;
        });
    }

    async requestYmm(
        modelSeoKey: string,
        modelYr: string
    ): Promise<RecallsApiResponse> {
        const {
            currentRegionCode,
            current3LetterCountryCode,
            currentLanguageRegionCode,
            brand,
        } = ServiceHandler.AppConfigurationService;
        const vehicleYmm = await getAemVehicleModelData(
            brand,
            currentRegionCode,
            currentLanguageRegionCode,
            modelYr,
            modelSeoKey,
            'SEO_KEY'
        );
        const model = vehicleYmm?.model;
        const params = {
            model,
            modelYr,
            country: current3LetterCountryCode.toUpperCase(),
            brand: brand,
            region: currentLanguageRegionCode,
        };
        const ymmUrl = DSL_BASE_URL + DSL_API_PATH.RECALLS_YMM;
        const response = HttpService.get<RecallsApiResponse>(ymmUrl, true, {
            params,
        });
        response.catch(error => {
            console.error(error);
        });
        return response.then(response => {
            return response.data;
        });
    }

    async getRecallsTradeInUrl(
        currentDomain: string
    ): Promise<TradeInLinkResponse> {
        const tradeInUrl = DSL_BASE_URL + DSL_API_PATH.TRADE_IN_LINK;

        const {
            currentCountryCode,
            currentLanguageRegionCode,
        } = ServiceHandler.AppConfigurationService;
        const params = {
            countryCode: currentCountryCode,
            language: currentLanguageRegionCode,
            referrerUrl: currentDomain,
        };

        const response = HttpService.get<TradeInLinkResponse>(
            tradeInUrl,
            true,
            {
                params,
            }
        );

        response.catch(error => {
            console.error(error);
            return;
        });

        return response.then(response => {
            return response.data;
        });
    }

    private nhtsaResponse(
        response: AxiosResponse<RecallsApiResponse>,
        sendSlackAlert: boolean,
        currentLanguageCode: string
    ) {
        response.data.nhtsa.forEach(nhtsa => {
            if (nhtsa.descriptionLang === null || !nhtsa.descriptionLang) {
                nhtsa.descriptionLang = nhtsa.description;
                sendSlackAlert = currentLanguageCode !== 'en';
            }
        });
        return sendSlackAlert;
    }
}
